/// <amd-module name="Artifacts/Feed/helpers/PackageCommandsHelper" />
import { FeedHubActions } from "Artifacts/Feed/redux/Actions";
import * as R from "Artifacts/Feed/Resources";
import { DeleteModalId, IDeleteModalProps } from "Artifacts/Root/components/DeleteModal";
import { DeprecateModalId, IDeprecateModalProps } from "Artifacts/Root/components/DeprecateModal";
import { IPromoteModalProps, PromoteModalId } from "Artifacts/Root/components/PromoteModal";
import {
  IUnlistRelistModalProps,
  UnlistRelistModalId,
} from "Artifacts/Root/components/UnlistRelistModal";
import { UnyankYankModalId } from "Artifacts/Root/components/UnyankYankModal";
import { injectModal } from "Artifacts/Root/protocols/Commands";
import { filterCommandsByTag } from "Artifacts/Root/protocols/CrossProtocolTools";
import * as Tags from "Artifacts/Root/protocols/TagConstants";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { ICommand } from "Artifacts/Root/protocols/types/ICommand";
import { getStore } from "Artifacts/Root/Redux";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

const tagFilters = new Set<string>([Tags.PackageList]);

export function getPromoteModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const promoteModalInjector = injectModal<IPromoteModalProps>(
    pageContext,
    "promote",
    PromoteModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onPromote: (viewId: string) => {
        return () => {
          command.action({
            feed,
            selectedVersions,
            viewId,
            onSuccessCallback: (selectedVersions: IPackageVersionIdentity[], viewName?: string) => {
              viewName && dispatch(FeedHubActions.fakePackagePromote(selectedVersions, viewName));
              hideModal();
            },
          });
        };
      },
      onDismiss: hideModal,
    }),
  );
  return promoteModalInjector;
}

export function getDeleteModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const deleteModalInjector = injectModal<IDeleteModalProps>(
    pageContext,
    "delete",
    DeleteModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onDelete: () => {
        command.action({
          feed,
          selectedVersions,
          onSuccessCallback: (selectedVersions: IPackageVersionIdentity[]) => {
            dispatch(FeedHubActions.fakePackageDelete(selectedVersions));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return deleteModalInjector;
}

export function getUnlistRelistModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
  commandId: string,
  modalId: string,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const unlistRelistModalInjector = injectModal<IUnlistRelistModalProps>(
    pageContext,
    commandId,
    modalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onUnlistRelist: () => {
        command.action({
          feed,
          selectedVersions,
          onSuccessCallback: (selectedVersions: IPackageVersionIdentity[], isListed: boolean) => {
            dispatch(FeedHubActions.fakePackageListUnlist(selectedVersions, isListed));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return unlistRelistModalInjector;
}

export function getDeprecateModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const deprecateModalInjector = injectModal<IDeprecateModalProps>(
    pageContext,
    "deprecate",
    DeprecateModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onDeprecate: (deprecateMessage: string) => {
        command.action({
          feed,
          selectedVersions,
          deprecateMessage,
          onSuccessCallback: () => {
            announce(getAnnouncement(R.PackageCommand_Announcement_Deprecated, selectedVersions));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return deprecateModalInjector;
}

export function getPackageListCommands(
  pageContext: IVssPageContext,
  protocol: IProtocol,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): ICommand[] {
  const promoteModalInjector = getPromoteModalInjector(pageContext, selectedVersions, feed);

  const deleteModalInjector = getDeleteModalInjector(pageContext, selectedVersions, feed);

  const unlistRelistModalInjector = getUnlistRelistModalInjector(
    pageContext,
    selectedVersions,
    feed,
    "unlistRelist",
    UnlistRelistModalId,
  );

  const unyankYankModalInjector = getUnlistRelistModalInjector(
    pageContext,
    selectedVersions,
    feed,
    "unyankYank",
    UnyankYankModalId,
  );
  const deprecateModalInjector = getDeprecateModalInjector(pageContext, selectedVersions, feed);

  const aggregateCommands = unlistRelistModalInjector(
    unyankYankModalInjector(
      deleteModalInjector(
        promoteModalInjector(
          deprecateModalInjector(
            filterCommandsByTag(
              protocol.getCommands(pageContext, selectedVersions, feed),
              tagFilters,
            ),
          ),
        ),
      ),
    ),
  );

  return aggregateCommands;
}

function getAnnouncement(
  protocolResourceString: string,
  selectedPackages: IPackageVersionIdentity[],
): string {
  if (selectedPackages.length > 1) {
    return Utils_String.format(
      R.PackageCommand_Announcement_Multiple,
      selectedPackages.length,
      protocolResourceString,
    );
  } else {
    return Utils_String.format(
      R.PackageCommand_Announcement,
      selectedPackages[0].name,
      selectedPackages[0].version,
      protocolResourceString,
    );
  }
}
